import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Button from "@amzn/meridian/esm/components/button";
import Box from "@amzn/meridian/esm/components/box";
import Column from "@amzn/meridian/esm/components/column";
import Loader from "@amzn/meridian/esm/components/loader";
import Row from "@amzn/meridian/esm/components/row";
import Text from "@amzn/meridian/esm/components/text";
import Theme from "@amzn/meridian/esm/components/theme/theme";
import InformSummarizeModal from "./InformSummarizeModal";
import SummarizingIndicator from "./SummarizingIndicator";
import SummarizeStopped from "./SummarizeStopped";
import SummaryReady from "./SummaryReady";
import aimsTheme from "../../meridian-theme-aims.json";
import star from "../../images/star.svg";
import errorIcon from "../../images/error_icon.svg";
import zoomSdk from "@zoom/appssdk";
import ServerNotRespondingModal from "../common/ServerNotRespondingModal";
import LogManager from "../utils/LogManager";
import { AIMS_STATUS, TRANSCRIPT_START_CHAT_MESSAGE } from "../../constants";
import { fetchRtmsSessionStatus, SESSION_STATUS, SESSION_STATUS_MESSAGE } from "../utils/RtmsSessionStatusUtils";
import { fetchSummaries } from "../utils/SummaryUtil";
import { fetchRtmsStatusUpdate } from "../utils/RtmsStatusUpdateUtil";
import RtmsErrorModal from "./RtmsErrorModal";
import ProductFeedback from "../common/feedback/ProductFeedback";
import { FEEDBACK_PAGE } from "../common/constants";

/**
 * This component is shown to the meeting host when AiMS is available for the meeting.
 *
 * Key Features:
 * - Renders a "Summarize" button to start RTMS.
 * - On first click, it shows an informational modal (`InformSummarizeModal`) to confirm the user understands the consent.
 * - On subsequent clicks, it triggers `startRTMS` via Zoom SDK.(TODO: will update to call backend api)
 * - Once summarization is active, the UI switches to an "End" button which stops RTMS.
 * - If RTMS start/stop fails, a `ServerNotRespondingModal` is shown for error feedback.
 * - When summarization ends, a temporary confirmation banner (`SummarizeStopped`) appears for 2 minutes.
 * - A `SummarizingIndicator` is shown during active summarization.
 */

const SummarizeBox = ({ isSummarizing, setIsSummarizing, meetingId, meetingUuid, aimsStatus, role, alias, appContext, initialIsRtmsStartedByOther, initialRtmsRequesterName }) => {
  const navigate = useNavigate();
  const [isInformSummarizeModalOpen, setIsInformSummarizeModalOpen] = useState(false);
  const [summaryStopped, setSummaryStopped] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [summarizeClickCount, setSummarizeClickCount] = useState(0);
  const [isServerErrorModalOpen, setIsServerErrorModalOpen] = useState(false);
  const [summaryReady, setSummaryReady] = useState(false);
  const [aimsUuid, setAimsUuid] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [isRtmsErrorModalOpen, setIsRtmsErrorModalOpen] = useState(false);
  const [rtmsErrorType, setRtmsErrorType] = useState(null);
  const [isRejected, setIsRejected] = useState(false);
  const [rtmsRequesterName, setRtmsRequesterName] = useState(null);
  const [isRtmsStartedByOther, setIsRtmsStartedByOther] = useState(false);
  const [rtmsStreamId, setRtmsStreamId] = useState(null);
  const [summaryReviewers, setSummaryReviewers] = useState(null);
  const [isProductFeedbackModalOpen, setIsProductFeedbackModalOpen] = useState(false);

  
  // Reference to store the timeout for hiding the SummarizeStopped component
  const hideStoppedComponentTimerRef = useRef(null);
  // Reference to store the RTMS start timestamp to avoid state timing issues
  const rtmsStartTimestampRef = useRef(null);
  // References for polling session status after summarize click
  const sessionStatusIntervalRef = useRef(null);
  const sessionStatusTimeoutRef = useRef(null);
  // Reference for rejection timeout (2 minutes)
  const rejectionTimeoutRef = useRef(null);

  /**
   * Reset component to clean state - used when RTMS starts successfully
   */
  const resetToCleanState = () => {
    // Clear all error states
    setIsServerErrorModalOpen(false);
    setIsRtmsErrorModalOpen(false);
    setRtmsErrorType(null);
    
    // Clear rejection state completely
    setIsRejected(false);
    // Note: Don't clear rtmsRequesterName here as it's needed for SummarizingIndicator
    
    // Clear summary-related states
    setSummaryReady(false);
    setSummaryStopped(false);
    
    // Clear loading state
    setIsLoading(false);
    
    // Clear any polling intervals/timeouts
    if (sessionStatusIntervalRef.current) {
      clearInterval(sessionStatusIntervalRef.current);
      sessionStatusIntervalRef.current = null;
    }
    if (sessionStatusTimeoutRef.current) {
      clearTimeout(sessionStatusTimeoutRef.current);
      sessionStatusTimeoutRef.current = null;
    }
    if (rejectionTimeoutRef.current) {
      clearTimeout(rejectionTimeoutRef.current);
      rejectionTimeoutRef.current = null;
    }
  };

  /**
   * Handle RTMS status change events from Zoom SDK
   * @param {object} event - The RTMS status change event
   */
  async function handleRTMSChange(event) {
    LogManager.info("RTMS changed:", JSON.stringify(event));
    LogManager.info(`[SummarizeBox] RTMS status changed to: ${event.status}`);
    
    if (event.status && event.status === "started") {
      // Check who started the session by calling the session status API
      try {
        const sessionStatus = await fetchRtmsSessionStatus(meetingUuid);
        LogManager.info("Session status when started:", sessionStatus);
        
        // Save the rtmsStreamId when RTMS is active
        if (sessionStatus.rtmsStreamId) {
          setRtmsStreamId(sessionStatus.rtmsStreamId);
          LogManager.info("Saved rtmsStreamId:", sessionStatus.rtmsStreamId);
        }
        
        // Check if the session was started by someone else
        if (!sessionStatus.rtmsRequesterAlias) {
          // No requester info available - this is problematic, hide summarize button
          setIsRtmsStartedByOther(true); // Hide the summarize button
          setRtmsRequesterName("Someone");
        } else if (sessionStatus.rtmsRequesterAlias !== alias) {
          // Started by someone else
          setIsRtmsStartedByOther(true);
          setRtmsRequesterName(sessionStatus.rtmsRequesterName || "Someone");
        } else {
          // Started by current user
          setIsRtmsStartedByOther(false);
        }
      } catch (error) {
        LogManager.error("Error fetching session status on RTMS start:", error);
        // Fallback: hide summarize button if we can't determine who started it
        setIsRtmsStartedByOther(true);
        setRtmsRequesterName("Someone");
      }
      
      // Reset to clean state and set summarizing to true
      resetToCleanState();
      setIsSummarizing(true);
      
      // Record the timestamp when summarization starts
      const startTimestamp = Math.floor(Date.now() / 1000);
      rtmsStartTimestampRef.current = startTimestamp;
      LogManager.info("RTMS started at timestamp:", startTimestamp);
    } else if (event.status && event.status === "stopped") {
      setIsSummarizing(false);
      setIsLoading(false); // Clear loading state when RTMS stops
      
      // Show product feedback modal when RTMS stops
      setIsProductFeedbackModalOpen(true);
      
      // Clear rejection state when RTMS stops, but preserve requester info for SummarizeStopped component
      setIsRejected(false);
      
      // Reset isRtmsStartedByOther immediately so organizers and alt hosts can see the summary button
      setIsRtmsStartedByOther(false);
      setRtmsRequesterName(null);
      
      // Fetch session status to check for errors and get reviewer data
      fetchRtmsSessionStatus(meetingUuid).then((sessionData) => {
        LogManager.info(`Session status: ${sessionData.status}, statusMessage: ${sessionData.statusMessage}`);
        
        // Extract reviewer data from session API response
        if (sessionData.reviewers) {
          LogManager.info("Summary reviewer data from session API:", sessionData.reviewers);
          setSummaryReviewers(sessionData.reviewers);
        }
        
        if (sessionData.statusMessage === SESSION_STATUS_MESSAGE.RTMS_INIT_ERROR || 
            sessionData.statusMessage === SESSION_STATUS_MESSAGE.RTMS_RUNTIME_ERROR) {
          // Show error modal for init or runtime errors only if current user started RTMS
          if (!isRtmsStartedByOther) {
            setRtmsErrorType(sessionData.statusMessage);
            setIsRtmsErrorModalOpen(true);
          }
        } else {
          // Normal stop - show stopped component and check for summary
          setSummaryStopped(true);
          
          // Start the summary checking process after RTMS stops
          setTimeout(async () => {
            const queryStartTime = rtmsStartTimestampRef.current - 10; 
            const queryEndTime = rtmsStartTimestampRef.current + 60;
            
            LogManager.info(`Using rtmsStartTimestamp: ${rtmsStartTimestampRef.current} for summary query`);
            const found = await checkForSummary(queryStartTime, queryEndTime);
            if (!found) {
              clearTimeout(hideStoppedComponentTimerRef.current);
              hideStoppedComponentTimerRef.current = setTimeout(() => {
                if (!summaryReady) {
                  setSummaryStopped(false);
                }
              }, 120000); // 2 minutes from now
            }
          }, 60000); // 1 minute
          
          // Show SummaryStopped component for 2 minutes (if summary not found)
          hideStoppedComponentTimerRef.current = setTimeout(() => {
            if (!summaryReady) {
              setSummaryStopped(false);
            }
          }, 60000);
        }
      }).catch(error => {
        LogManager.error("Error fetching session status:", error);
        // Fallback to normal behavior if status fetch fails
        setSummaryStopped(true);
      });
    }
  }

  // Initialize state with values from Home.jsx when component mounts
  useEffect(() => {
    if (initialIsRtmsStartedByOther !== undefined) {
      setIsRtmsStartedByOther(initialIsRtmsStartedByOther);
    }
    if (initialRtmsRequesterName !== undefined) {
      setRtmsRequesterName(initialRtmsRequesterName);
    }
  }, [initialIsRtmsStartedByOther, initialRtmsRequesterName]);

  useEffect(() => {
    // Localhost testing override
    if (window.location.href === "http://localhost:3000/") {
      setIsInformSummarizeModalOpen(false);
      setSummaryStopped(false);
      setIsServerErrorModalOpen(false);
    } else {
      // Set up RTMS status change listener for non-localhost environments
      try {
        LogManager.info(`[SummarizeBox] Setting up RTMS status change listener for meeting: ${meetingUuid}`);
        zoomSdk.onRTMSStatusChange(handleRTMSChange);
      } catch (error) {
        LogManager.error("[SummarizeBox] Error setting up RTMS status listener:", error);
      }
    }
    
    // Cleanup function to clear the timeout when component unmounts
    return () => {
      if (hideStoppedComponentTimerRef.current) {
        clearTimeout(hideStoppedComponentTimerRef.current);
      }
      if (sessionStatusIntervalRef.current) {
        clearInterval(sessionStatusIntervalRef.current);
      }
      if (sessionStatusTimeoutRef.current) {
        clearTimeout(sessionStatusTimeoutRef.current);
      }
      if (rejectionTimeoutRef.current) {
        clearTimeout(rejectionTimeoutRef.current);
      }
    };
  }, [meetingUuid]);

  // Function to check for summary after RTMS has ended
  const checkForSummary = async (queryStartTime, queryEndTime) => {
    LogManager.info(`Checking for summary with time between ${queryStartTime} and ${queryEndTime}`);
    
    try {
      // Override the default time range in fetchSummaries with our specific time range
      const summaryListJson = await fetchSummaries(queryStartTime, queryEndTime, 1);
      
      if (summaryListJson && summaryListJson.summaries && summaryListJson.summaries.length > 0) {
        // Found a summary that matches our criteria
        const summary = summaryListJson.summaries[0];
        if (summary.meetingUuid === meetingUuid) {
          LogManager.info("Found matching summary:", summary);
          setAimsUuid(summary.aimsUuid);
          setSummaryData(summary);
          setSummaryStopped(false);
          setSummaryReady(true);
          return true;
        }
      }
      
      return false;
    } catch (error) {
      LogManager.error("Error checking for summary:", error);
      return false;
    }
  };

  //TODO: add more checks
  const onEndSummarize = async () => {
    if (isLoading) return;
    setIsLoading(true);
    LogManager.info("End button clicked, call stopping RTMS API...");

    let rtmsResponse = {};
    if (window.location.href === "http://localhost:3000/") {
      console.log("[Localhost] Simulating stop RTMS call.");
      await new Promise(resolve => setTimeout(resolve, 2000)); // 2 seconds delay
      rtmsResponse.result = "OK"; // or "Fail" to simulate error
      // Note: State changes will be handled by handleRTMSChange event listener
    } else {
      try {
        rtmsResponse = await fetchRtmsStatusUpdate("stop", meetingUuid, meetingId, role, appContext);
      } catch (e) {
        console.error(e);
        rtmsResponse = "Error";
      }
    }

    if (rtmsResponse.result !== "OK") {
      setIsLoading(false); // Clear loading state on error
      LogManager.info(`RTMS stop request failed with result: ${rtmsResponse.result}, showing server error modal`);
      setIsServerErrorModalOpen(true);
      return;
    }

    // Note: State changes (setIsSummarizing, setSummaryStopped, summary checking) 
    // are now handled automatically by the handleRTMSChange event listener
    // Loading state will be cleared when RTMS status change event is received
    LogManager.info("RTMS stop request successful. State changes will be handled by event listener.");

    // Don't clear loading state here for successful requests - let handleRTMSChange do it
  };

  const onModalConfirmed = () => {
    setIsInformSummarizeModalOpen(false);
    sendTranscriptStartToChat();
    onSummarize();
  };

  const sendTranscriptStartToChat = async () => {
    try {
      await zoomSdk.sendMessageToChat({
        message: TRANSCRIPT_START_CHAT_MESSAGE
      });
      LogManager.info("Sent transcription disclaimer message");
    } catch (error) {
      LogManager.error("Error sending disclaimer message:", error);
    }
  };

  const handleRtmsErrorCancel = () => {
    // Cancel: Close modal and reset to original state (show Summarize button)
    setIsRtmsErrorModalOpen(false);
    setRtmsErrorType(null);
    // Reset all states to original
    setIsSummarizing(false);
    setSummaryStopped(false);
    setSummaryReady(false);
    setIsLoading(false);
    // Clear requester information
    setIsRtmsStartedByOther(false);
    setRtmsRequesterName(null);
    LogManager.info("User cancelled RTMS error modal - reset to original state");
  };

  const handleRtmsErrorRestart = () => {
    // Restart: Close modal and restart summarization
    setIsRtmsErrorModalOpen(false);
    setRtmsErrorType(null);
    // Reset states and restart
    setIsSummarizing(false);
    setSummaryStopped(false);
    setSummaryReady(false);
    setIsLoading(false);
    // Clear requester information before restarting
    setIsRtmsStartedByOther(false);
    setRtmsRequesterName(null);
    LogManager.info("User clicked restart from RTMS error modal - restarting summarization");
    // Start summarization immediately
    onSummarize();
  };

  //TODO: Add more checks
  const onSummarize = async () => {
    if (isLoading) return;
    setIsLoading(true);
    setSummaryStopped(false);
    LogManager.info("Attempting to start RTMS...");

    let rtmsResponse = {};
    if (window.location.href === "http://localhost:3000/") {
      console.log("[Localhost] Simulating startRTMS call: Success");
      await new Promise(resolve => setTimeout(resolve, 2000)); // 2 seconds delay
      rtmsResponse.result = "OK";
    } else {
      try {
        rtmsResponse = await fetchRtmsStatusUpdate("start", meetingUuid, meetingId, role, appContext);
      } catch (e) {
        LogManager.error("Error calling /rtms-status-update:", e);
        rtmsResponse = "Error";
      }
    }

    LogManager.info("The /rtms-status-update response:", rtmsResponse);

    if (rtmsResponse.result === "OK") {
      setSummarizeClickCount((prev) => prev + 1);
      // Start polling session status every 5 seconds
      LogManager.info("RTMS start request successful. Starting session status polling...");
      
      // Clear any existing intervals/timeouts
      if (sessionStatusIntervalRef.current) {
        clearInterval(sessionStatusIntervalRef.current);
      }
      if (sessionStatusTimeoutRef.current) {
        clearTimeout(sessionStatusTimeoutRef.current);
      }
      
      // Set up polling every 5 seconds
      sessionStatusIntervalRef.current = setInterval(async () => {
        try {
          const sessionStatus = await fetchRtmsSessionStatus(meetingUuid);
          LogManager.info(`Session status polling result: ${sessionStatus.status}, statusMessage: ${sessionStatus.statusMessage}`);
          
          if (sessionStatus.status === SESSION_STATUS.ACTIVE) {
            // Status is now active, save rtmsStreamId and update state
            if (sessionStatus.rtmsStreamId) {
              setRtmsStreamId(sessionStatus.rtmsStreamId);
              LogManager.info("Saved rtmsStreamId during polling:", sessionStatus.rtmsStreamId);
            }
            
            clearInterval(sessionStatusIntervalRef.current);
            clearTimeout(sessionStatusTimeoutRef.current);
            setIsLoading(false);
            setIsSummarizing(true);
            LogManager.info("Session status is now ACTIVE, stopping polling");
          } else if (sessionStatus.statusMessage === SESSION_STATUS_MESSAGE.RTMS_INIT_ERROR || 
                     sessionStatus.statusMessage === SESSION_STATUS_MESSAGE.RTMS_RUNTIME_ERROR) {
            // Error detected during polling, show error modal
            clearInterval(sessionStatusIntervalRef.current);
            clearTimeout(sessionStatusTimeoutRef.current);
            setIsLoading(false);
            setRtmsErrorType(sessionStatus.statusMessage);
            setIsRtmsErrorModalOpen(true);
            LogManager.info(`RTMS error detected during polling: ${sessionStatus.statusMessage}, showing error modal`);
          }
        } catch (error) {
          LogManager.error("Error polling session status:", error);
        }
      }, 5000);
      
      // Set up timeout to show error modal after 15 seconds if still INACTIVE
      sessionStatusTimeoutRef.current = setTimeout(async () => {
        try {
          const sessionStatus = await fetchRtmsSessionStatus(meetingUuid);
          if (sessionStatus.status !== SESSION_STATUS.ACTIVE) {
            // Still inactive after 15 seconds, show error modal
            clearInterval(sessionStatusIntervalRef.current);
            setIsLoading(false);
            setIsServerErrorModalOpen(true);
            LogManager.info("Session status still INACTIVE after 15 seconds, showing error modal");
          }
        } catch (error) {
          LogManager.error("Error checking final session status:", error);
          clearInterval(sessionStatusIntervalRef.current);
          setIsLoading(false);
          setIsServerErrorModalOpen(true);
        }
      }, 15000);
    } else if (rtmsResponse.result === "REJECTED") {
      // Someone else already started the summary
      setIsLoading(false);
      setIsRejected(true);
      const requesterName = rtmsResponse.rtmsRequesterName || "Someone";
      setRtmsRequesterName(requesterName);
      LogManager.info(`RTMS start rejected - ${requesterName} is already starting summary`);
      
      // Set up 2-minute timer to reset to Summarize button if no RTMS "started" event is received
      rejectionTimeoutRef.current = setTimeout(() => {
        LogManager.info("2-minute rejection timeout reached, resetting to Summarize button");
        setIsRejected(false);
        setRtmsRequesterName(null);
      }, 120000); // 2 minutes
    } else {
      // Handle all other error cases (FAIL, Error, etc.)
      setIsLoading(false); // Clear loading state on error
      setIsServerErrorModalOpen(false);
      setTimeout(() => setIsServerErrorModalOpen(true), 0);
    }
  };

  return (
    <Theme palette={aimsTheme}>
      <Column alignmentHorizontal="start" spacing="none" minHeight="630px" heights={["fit", "fill","fit"]}>
        <Box>
          <Column height="40px"/>
          {/* Hide the summarize button box if session is started by someone else */}
          {!isRtmsStartedByOther && (
            <Column height="100px" spacing="none">
              <Row width="100%" spacing="none" spacingInset="none">
                <Column width="72px"/>
                <Theme tokens={{ boxBorderColorOutline: "#E7E9E9" }}>
                  <Box type="outline" spacingInset="400" width="226px" height="100px">
                  <Row spacing="none">
                    <img src={star} alt="star"/>
                    <Column width="20px"/>
                    <Text fontFamily="amazonEmber" type="h50" alignment="start">
                      Summarize meeting
                    </Text>
                  </Row>
                  <Column height="12px"></Column>
                  <Theme
                    tokens={{
                      buttonBackgroundColorPrimaryDefault: isSummarizing ? "#AF1F18" : "#006D5B",
                      buttonBackgroundColorPrimaryHover: isSummarizing ? "#8C1913" : "#005345",
                      buttonBackgroundColorPrimaryPressed: isSummarizing ? "#6E140F" : "#003F33",
                    }}
                  >
                    {isLoading ? (
                      <Row alignmentHorizontal="center">
                        <Theme palette={aimsTheme}>
                          <Loader size="medium"/>
                        </Theme>
                      </Row>
                    ) : isSummarizing ? (
                      <Button size="small" minWidth="194px" onClick={onEndSummarize}>
                        End
                      </Button>
                    ) : isRejected ? (
                      <Row alignmentHorizontal="center">
                        <img src={errorIcon} alt="error" style={{ display: "block", margin: "auto", width: "25.6px", height: "auto" }}/>
                      </Row>
                    ) : (
                      <>
                        <Button
                          size="small"
                          minWidth="194px"
                          onClick={() => {
                            if (summarizeClickCount === 0) {
                              setIsInformSummarizeModalOpen(true);
                            } else {
                              onSummarize(); // second+ click: directly start summarize
                            }
                          }}
                        >
                          Summarize
                        </Button>
                      </>
                    )}
                  </Theme>
                  {isServerErrorModalOpen ? (
                    <ServerNotRespondingModal isOpen={true} onClose={() => setIsServerErrorModalOpen(false)} />
                  ) : (
                    <InformSummarizeModal
                      isOpen={isInformSummarizeModalOpen}
                      onClose={() => setIsInformSummarizeModalOpen(false)}
                      onConfirm={onModalConfirmed}
                    />
                  )}
                  {isRtmsErrorModalOpen &&
                    <RtmsErrorModal
                      isOpen={isRtmsErrorModalOpen}
                      onCancel={handleRtmsErrorCancel}
                      onRestart={handleRtmsErrorRestart}
                      errorType={rtmsErrorType}
                    />
                  }
                  </Box>
                </Theme>
              </Row>
            </Column>
          )}
          {isRejected && rtmsRequesterName && (
            <>
              <Column height="16px"/>
              <Row width="100%" spacing="none" spacingInset="none">
                <Column width="72px"/>
                <Column spacing="none">
                  <Row alignmentHorizontal="start">
                    <Text fontFamily="amazonEmber" type="h50" color="secondary">
                      {rtmsRequesterName}
                    </Text>
                  </Row>
                  <Row alignmentHorizontal="start">
                    <Text fontFamily="amazonEmber" type="b200" color="secondary">
                      is turning on meeting summary
                    </Text>
                  </Row>
                </Column>
              </Row>
            </>
          )}
          <Column height="32px"/>
          {summaryStopped && !summaryReady && <SummarizeStopped 
            aimsStatus={aimsStatus} 
            role={role} 
            summaryReviewers={summaryReviewers}
            alias={alias}
            rtmsRequesterName={rtmsRequesterName}
          />}
          {summaryReady && <SummaryReady aimsUuid={aimsUuid} onViewSummary={() => {
            // Clear the timer when navigating to the summary page
            if (hideStoppedComponentTimerRef.current) {
              clearTimeout(hideStoppedComponentTimerRef.current);
              hideStoppedComponentTimerRef.current = null;
            }
            navigate(`/meeting/summary/${aimsUuid}`, { state: { summary: summaryData } });
          }} />}
          {isSummarizing && (
            <SummarizingIndicator 
              rtmsRequesterName={rtmsRequesterName} 
              isRtmsStartedByOther={isRtmsStartedByOther}
              alias={alias}
              rtmsStreamId={rtmsStreamId}
              meetingUuid={meetingUuid}
              meetingId={meetingId}
              aimsStatus={aimsStatus}
            />
          )}
        </Box>
        <Box/>
        <Box width="358px">
          <ProductFeedback 
            isModalOpen={isProductFeedbackModalOpen} 
            setIsModalOpen={setIsProductFeedbackModalOpen} 
            usePopover={true}
            page={FEEDBACK_PAGE.SUMMARIZE_END}
          />
        </Box>
      </Column>
    </Theme>
  );
};

export default SummarizeBox;
